/**
 * 분할 보기 v5 — 긴호흡 문단 행 정렬(docs/SPLITVIEW_ROWS_DESIGN.md D1).
 *
 * **순수 모듈이다.** 행 계산은 함수 하나(`buildRows`)로 끝나고, 상태를
 * 어디에도 저장하지 않는다 — 고아 승격조차 `now` 로 매번 다시 계산하는
 * 파생값이다(D1-4.4: 3분 뒤 문단이 도착하면 승격이 스스로 풀린다).
 *
 * 조인 키는 **이미 존재한다**(설계 §0): `FinalItem.utt` ↔
 * `RecordParagraphData.utt_ids` 의 결합은 리듀서가 `consolidatedInto` 로
 * 끝내 놓았다. 여기서 하는 일은 그 결과를 행으로 묶는 것뿐이고, 서버·
 * 리듀서·이벤트 계약은 한 글자도 바뀌지 않는다.
 */

import type { FeedItem, FinalItem, ParagraphItem } from './reducer'
import type { FeedOrder } from './feed-order'

export type RowKind = 'para' | 'pending' | 'orphan'

export interface FeedRow {
  kind: RowKind
  /** 안정 식별자 — 앵커 좌표계(D3-3). 'row-para-p-12' | 'row-pending' | 'row-orphan-<seq>' */
  id: string
  /** 행 정렬 좌표(D1-2.5). pending 은 정렬 제외 — null. */
  rowSeq: number | null
  /** 눈금(epoch ms, D2-3). pending 은 null. */
  ts: number | null
  /** 우측 — para 행만 값이 있다. */
  paragraph: ParagraphItem | null
  /** 좌측 — 항상 시간 오름차순(D2-2). */
  left: FeedItem[]
  /** 이전 회의 시딩분 전용 행(D1-4.6) — 고아 라벨을 붙이지 않는다. */
  prev: boolean
}

/**
 * 문단을 못 받기로 확정하는 문턱(D1-4.2).
 *
 * 도출: 창 상한 `WINDOW_CAP_S 60` + 정적 플러시 `WINDOW_QUIET_FLUSH_S 45`
 * (`server/longform.py:52-54`) = 최악 105s 뒤에 창이 닫히고, 거기서 복원이
 * 시작된다. 3분은 그 위에 75s 여유다. **FP 0 을 목표로 하는 축**이므로
 * (CLAUDE.md §1 규율 2) 일부러 압도적으로 보수적이다 — 승격이 빠를수록
 * 정상 발화에 `문단 없음` 이 붙고, 그러면 그 표기가 죽는다.
 */
export const ORPHAN_AFTER_MS = 180_000

/**
 * 문단↔멤버 역색인(D4-2) — `caption-feed.tsx` 의 useMemo 본문을 **그대로**
 * 옮긴 것이다. 두 화면(통합 피드·행 정렬)이 같은 계산을 봐야 하므로 복제가
 * 아니라 추출이다 — 복제는 드리프트의 시작이다.
 *
 * 장문 문단(record.paragraph)에 통합된 final 은 최상위 피드에서 숨기고 자기
 * 문단 카드의 아코디언 내용(members)으로 넘긴다. 문단이 하나도 없으면 이
 * 단계는 원본을 **항목 그대로** 통과시킨다 — 플래그 OFF 방의 DOM 은 오늘과
 * 동일하다.
 */
export function indexMembers(rawItems: readonly FeedItem[]): {
  visible: FeedItem[]
  membersByPara: Map<string, FinalItem[]>
  paraOf: Map<string, string>
} {
  const membersByPara = new Map<string, FinalItem[]>()
  const paraOf = new Map<string, string>()
  const visible: FeedItem[] = []
  for (const it of rawItems) {
    if (it.kind === 'final' && it.consolidatedInto) {
      const arr = membersByPara.get(it.consolidatedInto)
      if (arr) arr.push(it)
      else membersByPara.set(it.consolidatedInto, [it])
      paraOf.set(it.id, it.consolidatedInto)
      continue
    }
    visible.push(it)
  }
  // 아코디언은 시간순(오래된 것부터)으로 읽는다 — 원본 배열은 최신 우선.
  for (const arr of membersByPara.values()) arr.reverse();
  return { visible, membersByPara, paraOf }
}

/** 대기 행의 고정 id — 항상 정확히 1개다(D1-1). */
const PENDING_ROW_ID = 'row-pending'
/** 이전 회의 시딩분 전용 행(D1-4.6). */
const PREV_ROW_ID = 'row-pending-prev'

/** 이전 회의 시딩분인가(S23) — 이 항목들은 결합 자체가 제외돼 있다(§0.1-4). */
function isPrev(item: FeedItem): boolean {
  return 'prev' in item && item.prev === true
}

/**
 * 행 배열을 만든다 — 순수·멱등, 입력을 변이하지 않는다.
 *
 * `order` 는 **배열 방향만** 정한다. 행 내부(좌측 카드 순서)는 언제나 시간
 * 오름차순이다(D2-2) — 문단 본문이 그 순서로 쓰여 있어서, 안쪽까지 뒤집으면
 * 우측 문장과 좌측 카드가 역순으로 대응해 대조가 불가능해진다.
 */
export function buildRows(
  rawItems: readonly FeedItem[],
  opts: { now: number; order: FeedOrder },
): FeedRow[] {
  const { now, order } = opts
  const { membersByPara, paraOf } = indexMembers(rawItems)

  const rows: FeedRow[] = []
  /** 아직 어떤 문단에도 안 속한 것들 — 시간 오름차순으로 모은다. */
  const pendingLeft: FeedItem[] = []
  const prevLeft: FeedItem[] = []
  const orphanLeft: FeedItem[] = []

  // rawItems 는 최신 우선이다(리듀서 계약). 행 좌측은 시간 오름차순이므로
  // 뒤에서부터 읽어 그대로 push 하면 정렬이 공짜다.
  for (let i = rawItems.length - 1; i >= 0; i -= 1) {
    const it = rawItems[i]
    if (!it) continue
    if (it.kind === 'paragraph') continue
    if (it.kind === 'final' && paraOf.has(it.id)) continue
    if (isPrev(it)) {
      // 이전 회의분은 문단이 올 리가 없다 — 안 오는 것이 정상인 것에
      // 경고를 붙이면 표기가 죽는다(D1-4.6).
      prevLeft.push(it)
      continue
    }
    if (now - it.ts > ORPHAN_AFTER_MS) orphanLeft.push(it)
    else pendingLeft.push(it)
  }

  for (const it of rawItems) {
    if (it.kind !== 'paragraph') continue
    rows.push({
      kind: 'para',
      id: `row-para-${it.id}`,
      // 문단의 seq 는 "덮은 발화들이 있던 자리" 로 리듀서가 이미 정해
      // 두었다(reducer.ts:723) — 행 정렬에 ts 를 쓰면 리듀서가 이미 푼
      // 문제를 다시 풀면서 다른 답을 내게 된다(D1-2 근거).
      rowSeq: it.seq,
      ts: it.ts,
      paragraph: it,
      left: membersByPara.get(it.id) ?? [],
      prev: isPrev(it),
    })
  }

  if (orphanLeft.length > 0) {
    const rowSeq = orphanLeft.reduce(
      (max, it) => (it.seq !== null && it.seq > max ? it.seq : max),
      0,
    )
    rows.push({
      kind: 'orphan',
      id: `row-orphan-${rowSeq}`,
      rowSeq,
      // 눈금은 멤버 중 **최소** ts — 그 구간이 언제 시작했는지가 읽을 값이다.
      ts: orphanLeft.reduce(
        (min, it) => (it.ts < min ? it.ts : min),
        orphanLeft[0]!.ts,
      ),
      paragraph: null,
      left: orphanLeft,
      prev: false,
    })
  }

  // 행 정렬 — pending 은 여기 끼지 않는다(항상 최신 끝, D1-2.5).
  rows.sort((a, b) => (a.rowSeq ?? 0) - (b.rowSeq ?? 0))
  if (order === 'newest-first') rows.reverse()

  const pendingRow: FeedRow = {
    kind: 'pending',
    id: PENDING_ROW_ID,
    rowSeq: null,
    ts: null,
    paragraph: null,
    left: pendingLeft,
    prev: false,
  }
  // 대기 행은 **비어 있어도 존재한다**(D1-1) — 자막마다 임시 행을 만들면
  // 문단 도착 시 N개 행이 1개로 접히며 화면이 통째로 무너진다.
  if (order === 'newest-first') rows.unshift(pendingRow)
  else rows.push(pendingRow)

  if (prevLeft.length > 0) {
    const prevRow: FeedRow = {
      kind: 'pending',
      id: PREV_ROW_ID,
      rowSeq: null,
      ts: null,
      paragraph: null,
      left: prevLeft,
      prev: true,
    }
    // 이전 회의분은 언제나 가장 과거 쪽이다.
    if (order === 'newest-first') rows.push(prevRow)
    else rows.unshift(prevRow)
  }

  return rows
}

/**
 * 점프 해석(D3-6) — utt 가 속한 행의 id. 없는 utt 면 null.
 *
 * 지금 `onJump` 프롭은 코드에 없다(설계 D3-6). 이 함수는 그 경로가 추가될
 * 때를 위한 인터페이스 예약이며, 이번 범위는 순수 함수와 그 테스트까지다.
 */
export function rowOfUtt(utt: string, rows: readonly FeedRow[]): string | null {
  for (const row of rows) {
    for (const it of row.left) {
      if (it.utt === utt) return row.id
    }
  }
  return null
}
